/**
 * Resolve a typed reply against the latest open GroundTruth choice card.
 *
 * The card is clickable, but typing has to work too: "2", "Option 2", the
 * choice's label, and Yes / No on a binary card. What makes this worth a module
 * is the other half of the contract — a reply that is NOT a clean pick must
 * never be read as one:
 *
 *   selected   → a single, unambiguous choice. The only outcome that answers
 *                the card.
 *   invalid    → an attempted pick that points at nothing ("7" on a 3-option
 *                card, "yes" on a numbered one, "confirm" while a question is
 *                open). Nothing is sent; the choices are redisplayed.
 *   ambiguous  → could be more than one choice (a word two labels share, a
 *                number that disagrees with the label typed next to it).
 *                Nothing is sent; the choices are redisplayed.
 *   qualified  → a pick PLUS a change to it ("2 but only on the first row").
 *                Treated as a new request: sent to the agent as ordinary text,
 *                and the card closes.
 *   unrelated  → not an attempt to pick at all. Also sent as a new request.
 *
 * Deliberately conservative: when in doubt between "selected" and anything
 * else, it is not selected. A mis-pick costs a wrong preview; a refusal costs
 * one click.
 *
 * Pure — no React, no I/O — so every case is a one-line unit test.
 */

import { isPlainApproval } from './bulkIntent';

const NUMBER_WORDS = { one: 1, two: 2, three: 3, four: 4, five: 5 };
const ORDINALS = {
  first: 1, second: 2, third: 3, fourth: 4, fifth: 5,
  '1st': 1, '2nd': 2, '3rd': 3, '4th': 4, '5th': 5,
};
const YES = new Set(['yes', 'y', 'yep', 'yeah', 'yup', 'yes please', 'sure', 'ok', 'okay']);
const NO = new Set(['no', 'n', 'nope', 'nah', 'no thanks']);

// Politeness around a pick that does not change it. Stripped before matching,
// so "go with 2 please" is the same reply as "2".
const LEAD_PHRASES = [
  "i'll go with", 'i will go with', "i'll take", 'i will take', "i'll pick", "i'll choose",
  "i'd like", 'i would like', "i'd go with", 'i choose', 'i pick', 'i want',
  "let's go with", 'lets go with', "let's do", 'lets do', 'going with', 'go with', 'go for',
  'choose', 'pick', 'select', 'take', 'ok', 'okay', 'sure', 'alright', 'so', 'um', 'hmm', 'please',
];
const TRAIL_PHRASES = [
  'please', 'pls', 'plz', 'thanks', 'thank you', 'thx', 'ty', 'then',
  'is fine', 'is good', 'is right', 'is correct', 'works', 'works for me',
  'sounds good', 'looks good', 'looks right',
];

/** Lower-case, fold smart quotes, collapse whitespace. */
function normalize(text) {
  return String(text ?? '')
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Brackets, quotes and terminal punctuation off both ends. */
const stripEdges = (s) => s.replace(/^["'`([{\s]+/, '').replace(/["'`)\]}.,!?;:\s]+$/, '').trim();

/** Comparison key for labels: letters and digits only, single-spaced. */
const labelKey = (s) => normalize(s).replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

function stripFillers(input) {
  let t = stripEdges(input);
  for (let changed = true; changed;) {
    changed = false;
    for (const p of LEAD_PHRASES) {
      if (t.startsWith(`${p} `) || t.startsWith(`${p}, `)) {
        // The comma of "ok, 2" goes with the lead-in.
        t = stripEdges(t.slice(p.length).replace(/^[\s,]+/, ''));
        changed = true;
      }
    }
    for (const p of TRAIL_PHRASES) {
      if (t.endsWith(` ${p}`) || t.endsWith(`, ${p}`)) {
        t = stripEdges(t.slice(0, t.length - p.length));
        changed = true;
      }
    }
  }
  return t;
}

// A digit run may not continue into a date or ratio ("2026-09-01", "9/1"), or
// into letters — "2nd" is an ordinal, handled by ORDINAL_RE.
const NUM_TOKEN = '(\\d{1,3}(?![\\d/a-z-])|(?:one|two|three|four|five)\\b)';
const ORD_TOKEN = '(first|second|third|fourth|fifth|1st|2nd|3rd|4th|5th|last)\\b';
// "Option 2", "option #2", "choice 3", "#2". ("no. 2" is NOT supported — it
// reads as a "no".) The prefix is captured so "option 7" can be told apart from
// a bare 7, which might be a value.
const NUMBER_RE = new RegExp(`^(option|opt|choice|number|num|#)?\\s*#?\\s*${NUM_TOKEN}(.*)$`);
const ORDINAL_RE = new RegExp(`^(?:the\\s+)?${ORD_TOKEN}(?:\\s+(?:one|option|choice))?(.*)$`);
const NONE_REPLIES = new Set(['none', 'neither', 'none of them', 'none of those', 'none of these', 'none of the above']);
// "1 or 2", "2, 3", "option 1 and option 2" — more than one option named.
// "first or second" / "2 or the third" — ordinals count as option names too.
const MORE_PICKS_RE = /^(?:(?:\s*(?:,|\/|&|\bor\b|\band\b)\s*|\s+)+(?:the\s+)?(?:option|opt|choice|#)?\s*#?\s*(?:\d{1,3}|one|two|three|four|five|first|second|third|fourth|fifth|1st|2nd|3rd|4th|5th|last)\b(?:\s+(?:one|option|choice)\b)?)+$/;
const YES_AND_NO_RE = /^(?:yes|y)\s*(?:,|\/|or|and|&)\s*(?:no|n)$|^(?:no|n)\s*(?:,|\/|or|and|&)\s*(?:yes|y)$/;

const toNumber = (tok, count) => {
  if (tok === 'last') return count;
  if (ORDINALS[tok]) return ORDINALS[tok];
  if (NUMBER_WORDS[tok]) return NUMBER_WORDS[tok];
  return Number(tok);
};

/** The rest of the reply after a pick token, minus separators ("2 - Label"). */
const restOf = (s) => stripEdges(String(s || '').replace(/^[\s.):,\-–—]+/, ''));

const rangeText = (set) => (set.kind === 'binary'
  ? '1 for Yes or 2 for No'
  : `a number from 1 to ${set.choices.length}`);

/** One-line hint shown on the card, stating how it can be answered by typing. */
export function choiceTypingHint(set) {
  if (!set?.choices?.length) return '';
  return set.kind === 'binary'
    ? 'Click an option, or type Yes or No.'
    : `Click an option, or type its number from 1 to ${set.choices.length} or its name.`;
}

/**
 * @param {string} text        the user's reply
 * @param {object} choiceSet   { kind: 'binary'|'numbered', choices: [{ id, number, label }] }
 * @returns {{ kind: 'selected', choice, how }
 *         | { kind: 'invalid' | 'ambiguous', reason, candidates? }
 *         | { kind: 'qualified' | 'unrelated' }}
 */
export function resolveChoiceReply(text, choiceSet) {
  const choices = Array.isArray(choiceSet?.choices) ? choiceSet.choices : [];
  if (!choices.length) return { kind: 'unrelated' };
  const binary = choiceSet.kind === 'binary';
  const raw = normalize(text);
  if (!raw) return { kind: 'unrelated' };
  const core = stripFillers(raw);
  const byNumber = (n) => choices.find((c) => Number(c.number) === n) || null;
  const keyed = choices.map((c) => ({ choice: c, key: labelKey(c.label) }));

  // 1 ── the exact label ("Wawa Food Market Shoppers (brand)").
  const coreKey = labelKey(core);
  const exact = keyed.filter((k) => k.key && k.key === coreKey);
  if (exact.length === 1) return { kind: 'selected', choice: exact[0].choice, how: 'label' };
  if (exact.length > 1) {
    return {
      kind: 'ambiguous',
      reason: `"${text.trim()}" matches more than one option. Pick a number.`,
      candidates: exact.map((k) => k.choice.number),
    };
  }

  // 1b ── "none" / "neither" answers a None-of-these escape when there is one.
  if (NONE_REPLIES.has(core)) {
    const escape = choices.find((c) => c.role === 'none');
    if (escape) return { kind: 'selected', choice: escape, how: 'label' };
  }

  // 2 ── Yes / No.
  if (YES.has(core) || NO.has(core)) {
    if (binary) return { kind: 'selected', choice: byNumber(YES.has(core) ? 1 : 2), how: 'yesno' };
    return {
      kind: 'invalid',
      reason: `This question has numbered options, so "${text.trim()}" doesn't pick one. Choose ${rangeText(choiceSet)}.`,
    };
  }
  if (YES_AND_NO_RE.test(core)) {
    return { kind: 'ambiguous', reason: `"${text.trim()}" names both answers. Choose ${rangeText(choiceSet)}.` };
  }
  // "yes apply" / "yes go ahead" read as confirming a proposal, not as an
  // answer — same class as a bare "confirm" (step 4), checked before the
  // "yes, but…" rule below would send it on as a new request.
  if (isPlainApproval(core) && !YES.has(core) && !NO.has(core)) {
    return {
      kind: 'ambiguous',
      reason: `"${text.trim()}" doesn't answer the question above. Choose ${rangeText(choiceSet)}. `
        + 'To apply a change, use its Confirm button.',
    };
  }
  const yesNoLead = /^(yes|no)\b[\s,.!:;-]+(.+)$/.exec(core);
  if (yesNoLead) return { kind: 'qualified' };

  // 3 ── a number, "Option N", or an ordinal.
  const num = NUMBER_RE.exec(core);
  const ord = num ? null : ORDINAL_RE.exec(core);
  if (num || ord) {
    const explicit = Boolean(num?.[1]) || Boolean(ord);
    const n = num ? toNumber(num[2], choices.length) : toNumber(ord[1], choices.length);
    const rest = restOf(num ? num[3] : ord[2]);
    const target = byNumber(n);
    // Several options named at once cannot be resolved to one.
    if (rest && MORE_PICKS_RE.test(num ? num[3] : ord[2])) {
      return {
        kind: 'ambiguous',
        reason: `"${text.trim()}" names more than one option. Pick one: ${rangeText(choiceSet)}.`,
      };
    }
    if (!rest) {
      if (target) return { kind: 'selected', choice: target, how: explicit ? 'option' : 'number' };
      // A bare large number is a value ("5000"), not a pick.
      if (!explicit && n > 20) return { kind: 'unrelated' };
      return { kind: 'invalid', reason: `There's no option ${n}. Choose ${rangeText(choiceSet)}.` };
    }
    if (!target) {
      // "option 7 for the first row" still names an option that does not exist;
      // "7 rows are wrong" is just a sentence that starts with a number.
      return explicit
        ? { kind: 'invalid', reason: `There's no option ${n}. Choose ${rangeText(choiceSet)}.` }
        : { kind: 'unrelated' };
    }
    // "2 - Wawa Food Market Shoppers" — the label next to the number must agree.
    const restKey = labelKey(rest);
    if (restKey === keyed.find((k) => k.choice === target)?.key) {
      return { kind: 'selected', choice: target, how: 'option' };
    }
    const other = keyed.find((k) => k.choice !== target && k.key && k.key === restKey);
    if (other) {
      return {
        kind: 'ambiguous',
        reason: `Option ${target.number} is "${target.label}", but "${rest}" is option ${other.choice.number}. Pick one number.`,
        candidates: [target.number, other.choice.number],
      };
    }
    return { kind: 'qualified' };
  }

  // 4 ── a confirmation word while a question is open is not an answer to it,
  //      and must never slide through to approving a preview either.
  if (isPlainApproval(core) || isPlainApproval(raw)) {
    return {
      kind: 'ambiguous',
      reason: `"${text.trim()}" doesn't answer the question above. Choose ${rangeText(choiceSet)}. `
        + 'To apply a change, use its Confirm button.',
    };
  }

  // 5 ── part of a label, or a label plus more words.
  if (coreKey.length >= 3) {
    const padded = ` ${coreKey} `;
    const containsLabel = keyed.filter((k) => k.key && padded.includes(` ${k.key} `));
    if (containsLabel.length >= 1) return { kind: 'qualified' };
    const partial = keyed.filter((k) => k.key && ` ${k.key} `.includes(padded));
    if (partial.length === 1) {
      const { choice, key } = partial[0];
      if (coreKey.length / key.length >= 0.6) return { kind: 'selected', choice, how: 'label' };
      return {
        kind: 'ambiguous',
        reason: `Did you mean option ${choice.number}, "${choice.label}"? Click it or type ${choice.number}.`,
        candidates: [choice.number],
      };
    }
    if (partial.length > 1) {
      const nums = partial.map((k) => k.choice.number);
      return {
        kind: 'ambiguous',
        reason: `"${text.trim()}" matches options ${nums.join(' and ')}. Pick a number.`,
        candidates: nums,
      };
    }
  }

  return { kind: 'unrelated' };
}
